/**
 * Adaptateur « Arrière-garde » — A89 (v0.43.0).
 *
 * *Arrière-garde* (`arc_rearguard`, Arcanes) regarde la **position** des dés
 * Maîtres : les sorts de dégâts portés par les deux emplacements **équipés**
 * les plus hauts sont renforcés. (*Avant-garde*, son pendant sur les
 * emplacements 1 et 2, est retirée du jeu en v0.45, A93.)
 *
 * Le cœur expose `game.rearguardSlots()` ; il est lu **à l'exécution**. Sans
 * lui, le même résultat se déduit de `state.nodes` et de `state.equipped`.
 */

export const REARGUARD_NODE = 'arc_rearguard';

export type SlotTag = 'rearguard';

export const SLOT_TAG_LABEL: Record<SlotTag, string> = {
  rearguard: 'Arrière-garde',
};

/** Partie de la façade `Game` lue ici (tout est optionnel). */
interface SlotsGame {
  state: { nodes?: Record<string, number>; equipped?: readonly (string | null)[] };
  rearguardSlots?: unknown;
}

function level(game: SlotsGame, id: string): number {
  const n = game.state.nodes?.[id];
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
}

/** Appelle `game.rearguardSlots()` s'il existe ; `undefined` si absent ou en erreur. */
function callRearguardSlots(game: SlotsGame): unknown {
  const fn = game.rearguardSlots;
  if (typeof fn !== 'function') return undefined;
  try {
    return (fn as () => unknown).call(game);
  } catch {
    return undefined;
  }
}

function indexList(raw: unknown): number[] | null {
  if (!Array.isArray(raw)) return null;
  return raw.filter((n): n is number => Number.isInteger(n) && n >= 0);
}

/** Les deux emplacements équipés les plus hauts : `[]` sans le talent. */
export function rearguardSlotsOf(game: SlotsGame): number[] {
  const fromCore = callRearguardSlots(game);
  if (fromCore !== undefined) return indexList(fromCore) ?? [];
  if (level(game, REARGUARD_NODE) <= 0) return [];
  const equipped = game.state.equipped ?? [];
  const filled: number[] = [];
  equipped.forEach((id, i) => {
    if (id) filled.push(i);
  });
  return filled.slice(-2);
}

/** Sous-titres des emplacements, par indice. Jamais sur un emplacement vide. */
export function slotTags(game: SlotsGame): Map<number, SlotTag[]> {
  const tags = new Map<number, SlotTag[]>();
  const equipped = game.state.equipped ?? [];
  for (const i of rearguardSlotsOf(game)) {
    if (equipped[i]) tags.set(i, ['rearguard']);
  }
  return tags;
}

/** Texte du sous-titre : « Arrière-garde ». */
export function slotTagText(tags: readonly SlotTag[]): string {
  return tags.map((t) => SLOT_TAG_LABEL[t]).join(' · ');
}
